// src/context/DashboardShellContext.tsx
//
// ¿Qué «shell» (marco de navegación) usa el dashboard de ESTA org?
//
//   · 'classic' → el <Layout> de siempre. Es el valor de TODA org que no lo
//     configure: nada cambia para ellas (ni un request de más bloquea el render).
//   · 'orion'   → el shell nuevo <OrionShell> (barra lateral + Inicio).
//
// 🔒 OPT-IN POR DATOS, nunca por nombre de org en el código. La fuente es la
// asignación de template de la org en mcp_django (endpoint autenticado ya
// desplegado, org SIEMPRE del JWT):
//
//   GET /django-api/api/v1/admin/templates/assignment/all
//     → [{ usage_context, custom_overrides, … }]
//
//   Se lee, en este orden:
//     1. asignación usage_context="dashboard" → custom_overrides.dashboard
//        (o custom_overrides directo si trae `shell`)
//     2. asignación usage_context="website" (o "site") → custom_overrides.dashboard
//
//   Forma esperada:  custom_overrides.dashboard = {
//     "shell": "orion",                 // activa el shell
//     "theme": { light:{…}, dark:{…} }, // opcional; si falta se usa custom_overrides.theme
//     "model_label": "GPT-5.4"          // opcional; etiqueta informativa del modelo
//   }
//
// 🏛️ DEFAULT POR ARQUETIPO (sin nombre de org en el código): algunos giros nacen
// con el shell «Orion» aunque la org no tenga asignación de template — hoy
// `government` (Atención Ciudadana como apps del SO, mismo menú que ve la org).
// La config por DATOS de la org gana clave por clave; `shell: "classic"` en datos
// lo apaga. El arquetipo lo reporta ModulesProvider (GET /org/modules) vía
// `setArchetype`. Sirve también a usuarios sin acceso a las asignaciones (p. ej.
// directores, que reciben 403 en /admin/templates/assignment/all).
//
// El último valor resuelto se recuerda por org en localStorage para que la
// siguiente carga no parpadee. Si la lectura falla (403, red), se conserva lo
// último conocido (por defecto 'classic').
//
// 🧪 Override SOLO para desarrollo/capturas: existe únicamente si el build se
// hizo con VITE_DEV_SHELL=1 (el Dockerfile de producción no lo define, y Vite
// sustituye la constante en build → el código muerto desaparece del bundle).
// Con él, `?shell=orion|classic` fuerza el shell en esa pestaña.

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { djangoApiCall } from '@/lib/api';
import { orgIdFromToken } from '@/lib/api_superadmin';

export type DashboardShell = 'classic' | 'orion';

export interface ShellThemeMode {
  bg?: string;
  bg_muted?: string;
  fg?: string;
  fg_muted?: string;
  border?: string;
  acc1?: string;
  acc2?: string;
  acc3?: string;
  /** Barra lateral (opcional): fondo; el texto claro debe dar AA o se ignora. */
  sidebar_bg?: string;
  /** Acento de la barra lateral (ítem activo, foco). */
  sidebar_accent?: string;
  /** Degradado de botones principales (texto blanco, AA en ambos extremos o se ignora). */
  gradient_from?: string;
  gradient_to?: string;
}

export interface ShellTheme {
  light?: ShellThemeMode;
  dark?: ShellThemeMode;
}

export interface DashboardShellConfig {
  shell?: string;
  theme?: ShellTheme;
  model_label?: string;
  /**
   * Qué muestra la barra lateral (opcional):
   *  · 'orion'   (default) → la navegación fija del producto Orion (Inicio, Agentes, Sitio…).
   *  · 'modules' → los MISMOS módulos que la org ve hoy en el dashboard clásico, con sus
   *                pestañas, solo que con el diseño nuevo. Para clientes con su propio menú.
   */
  nav?: 'orion' | 'modules';
  /** false → sin el Inicio de Orion: la org aterriza donde aterriza hoy (default true). */
  home?: boolean;
  /** false → oculta la tarjeta «Impulsa el crecimiento…» de la barra lateral (default true). */
  promo?: boolean;
  /** Marca de la org en el shell (opcional; sin ella se usa la de Orion). */
  brand?: {
    logo_dark?: string;   // logo para la barra lateral oscura
    symbol?: string;      // isotipo: avatar del asistente e ícono
    favicon?: string;
    agent_avatar?: string; // avatar del agente flotante (opt-in; sin él, el orbe de Orion)
    name?: string;        // texto alternativo del logo
    assistant_name?: string; // «Asistente de {assistant_name}»
    app_icon?: string;     // ícono CUADRADO de la app instalable (https o ruta /icons/…); sin él, se dibuja desde el logo
    app_short_name?: string; // nombre bajo el ícono en el celular (≤12 letras se ve completo)
  };
  /** Chat del asistente: sugerencias del estado vacío y subtítulo (opcional). */
  assistant?: {
    suggestions?: string[];
    subtitle?: string;
  };
}

/** Config del shell que un arquetipo trae de fábrica (la org la puede sobreescribir por datos). */
export const ARCHETYPE_SHELL_DEFAULTS: Record<string, DashboardShellConfig> = {
  government: {
    shell: 'orion',
    nav: 'modules',
    assistant: {
      subtitle: 'Pregúntame por los reportes ciudadanos, los buzones o el padrón.',
      suggestions: [
        '¿Cuántos reportes abiertos hay hoy y de qué tipo?',
        '¿Qué colonias concentran más reportes este mes?',
        '¿Qué tickets llevan más tiempo sin atender?',
        '¿Cómo creo un agente para mi dependencia?',
      ],
    },
  },
};

/** Mezcla pura: default del arquetipo ← config de la org (gana la org, clave por clave). */
export function effectiveShellConfig(
  dataCfg: DashboardShellConfig | null,
  archetype: string | null,
): DashboardShellConfig | null {
  const base = archetype ? ARCHETYPE_SHELL_DEFAULTS[archetype] : undefined;
  if (!base) return dataCfg;
  if (!dataCfg) return { ...base };
  return {
    ...base,
    ...dataCfg,
    brand: dataCfg.brand ?? base.brand,
    theme: dataCfg.theme ?? base.theme,
    assistant: dataCfg.assistant ?? base.assistant,
  };
}

interface ShellCtx {
  shell: DashboardShell;
  /** `custom_overrides.dashboard` tal cual (solo claves conocidas se usan). */
  config: DashboardShellConfig | null;
  /** Paleta efectiva: dashboard.theme → custom_overrides.theme (sitio) → null. */
  theme: ShellTheme | null;
  /** true mientras se resuelve la primera lectura. */
  loading: boolean;
  refresh: () => void;
  /** ModulesProvider avisa el arquetipo de la org (para el default por giro). */
  setArchetype: (archetype: string | null) => void;
}

const Ctx = createContext<ShellCtx>({
  shell: 'classic',
  config: null,
  theme: null,
  loading: false,
  refresh: () => {},
  setArchetype: () => {},
});

const CACHE_PREFIX = 'orion_dashboard_shell:';

const DEV_SHELL_OVERRIDE = import.meta.env.VITE_DEV_SHELL === '1';

function devOverride(): DashboardShell | null {
  if (!DEV_SHELL_OVERRIDE) return null;
  try {
    const q = new URLSearchParams(window.location.search).get('shell');
    if (q === 'orion' || q === 'classic') {
      sessionStorage.setItem('dev_shell_override', q);
      return q;
    }
    const s = sessionStorage.getItem('dev_shell_override');
    return s === 'orion' || s === 'classic' ? s : null;
  } catch {
    return null;
  }
}

function readCache(org: string): DashboardShell {
  if (!org) return 'classic';
  try {
    return localStorage.getItem(CACHE_PREFIX + org) === 'orion' ? 'orion' : 'classic';
  } catch {
    return 'classic';
  }
}

function writeCache(org: string, shell: DashboardShell) {
  if (!org) return;
  try {
    localStorage.setItem(CACHE_PREFIX + org, shell);
  } catch {
    /* private mode */
  }
}

interface AssignmentDTO {
  usage_context: string;
  custom_overrides: Record<string, any> | null;
}

function isObj(v: unknown): v is Record<string, any> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/** Extrae {config, theme} de las asignaciones. Puro → fácil de razonar. */
export function resolveShellConfig(list: AssignmentDTO[]): {
  config: DashboardShellConfig | null;
  theme: ShellTheme | null;
} {
  const byCtx = (c: string) => list.find((a) => a?.usage_context === c)?.custom_overrides;
  const dashOv = byCtx('dashboard');
  // Algunas orgs guardan su asignación del sitio como "site" (p. ej. las de template ai-lab).
  const webOv = byCtx('website') ?? byCtx('site');

  let config: DashboardShellConfig | null = null;
  if (isObj(dashOv)) {
    if (isObj(dashOv.dashboard)) config = dashOv.dashboard;
    else if (typeof dashOv.shell === 'string') config = dashOv as DashboardShellConfig;
  }
  if (!config && isObj(webOv) && isObj(webOv.dashboard)) config = webOv.dashboard;

  const theme: ShellTheme | null =
    (config && isObj(config.theme) ? config.theme : null) ||
    (isObj(webOv) && isObj(webOv.theme) && (isObj(webOv.theme.light) || isObj(webOv.theme.dark))
      ? (webOv.theme as ShellTheme)
      : null);

  return { config, theme };
}

export function DashboardShellProvider({ children }: { children: React.ReactNode }) {
  const [org, setOrg] = useState<string>(() => orgIdFromToken());
  const [shell, setShell] = useState<DashboardShell>(() => devOverride() ?? readCache(orgIdFromToken()));
  const [config, setConfig] = useState<DashboardShellConfig | null>(null);
  const [theme, setTheme] = useState<ShellTheme | null>(null);
  const [loading, setLoading] = useState<boolean>(() => !!localStorage.getItem('auth_token'));
  // Config tal cual viene de DATOS (asignación de template) y arquetipo de la org.
  const [dataConfig, setDataConfig] = useState<DashboardShellConfig | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [archetype, setArchetypeState] = useState<string | null>(null);
  const setArchetype = useCallback((a: string | null) => setArchetypeState(a || null), []);

  const lastOrgRef = React.useRef<string>(orgIdFromToken());
  const load = useCallback(async () => {
    const currentOrg = orgIdFromToken();
    // Token u org nuevos: lo resuelto para la org anterior NO sirve (si no, su
    // shell/marca se escribiría en la caché de esta). El arquetipo lo
    // corrige ModulesProvider tras su propio refetch (si cambia, se re-resuelve).
    setDataConfig(null);
    setDataLoaded(false);
    if (lastOrgRef.current !== currentOrg) {
      lastOrgRef.current = currentOrg;
      setConfig(null);
      setTheme(null);
    }
    setOrg(currentOrg);
    if (!localStorage.getItem('auth_token') || !currentOrg) {
      setShell(devOverride() ?? 'classic');
      setConfig(null);
      setDataConfig(null);
      setDataLoaded(false);
      setTheme(null);
      setLoading(false);
      return;
    }
    setShell(devOverride() ?? readCache(currentOrg));
    setLoading(true);
    try {
      const list = await djangoApiCall<AssignmentDTO[]>('/admin/templates/assignment/all');
      const { config: cfg, theme: th } = resolveShellConfig(Array.isArray(list) ? list : []);
      setDataConfig(cfg);
      setDataLoaded(true);
      setTheme(th);
    } catch {
      // Sin acceso / red: sin datos de ESTA org (el default del arquetipo aún aplica);
      // el shell se queda en lo último conocido para esta org.
      setDataConfig(null);
      setDataLoaded(false);
      setShell(devOverride() ?? readCache(currentOrg));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const onReady = () => void load();
    const onLogout = () => {
      setShell(devOverride() ?? 'classic');
      setConfig(null);
      setDataConfig(null);
      setDataLoaded(false);
      setArchetypeState(null);
      setTheme(null);
    };
    window.addEventListener('auth:token-ready', onReady);
    window.addEventListener('auth:logout', onLogout);
    return () => {
      window.removeEventListener('auth:token-ready', onReady);
      window.removeEventListener('auth:logout', onLogout);
    };
  }, [load]);

  // Resolución final: datos de la org sobre el default de su arquetipo. Solo se
  // decide cuando hay algo que decidir (datos leídos o arquetipo conocido); si no,
  // se respeta lo último conocido (caché) para no parpadear.
  useEffect(() => {
    if (!dataLoaded && !archetype) return;
    const eff = effectiveShellConfig(dataLoaded ? dataConfig : null, archetype);
    // Sin datos leídos (p. ej. 403) y sin default de arquetipo → se queda la caché.
    if (!dataLoaded && !eff) return;
    const resolved: DashboardShell = eff?.shell === 'orion' ? 'orion' : 'classic';
    if (org) writeCache(org, resolved);
    setConfig(eff);
    setShell(devOverride() ?? resolved);
  }, [dataConfig, dataLoaded, archetype, org]);

  const value = useMemo<ShellCtx>(
    () => ({ shell, config, theme, loading, refresh: () => void load(), setArchetype }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shell, config, theme, loading, load, org, setArchetype],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useDashboardShell = () => useContext(Ctx);
